import { useState, useEffect, useCallback } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ExternalLink, TrendingUp, Clock, Globe, Search, BarChart3, Lightbulb, AlertTriangle } from "lucide-react";
import { fetchCompetitorPages, fetchCompetitorStats } from "@/lib/api";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COMPETITORS = [
  { key: 'all', label: 'All Competitors' },
  { key: 'jbhifi.com.au', label: 'JB Hi-Fi', color: '#FFEE2F' },
  { key: 'thegoodguys.com.au', label: 'The Good Guys', color: '#3B82F6' },
  { key: 'harveynorman.com.au', label: 'Harvey Norman', color: '#EF4444' },
  { key: 'officeworks.com.au', label: 'Officeworks', color: '#10B981' },
];

const COMP_COLORS = {
  'jbhifi.com.au': '#FFEE2F', 'thegoodguys.com.au': '#3B82F6',
  'harveynorman.com.au': '#EF4444', 'officeworks.com.au': '#10B981',
};

const CATEGORIES = [
  { key: 'all', label: 'All' },
  { key: 'sale_deals', label: 'Sales / Deals' },
  { key: 'tvs', label: 'TVs' },
  { key: 'phones', label: 'Phones' },
  { key: 'laptops', label: 'Laptops' },
  { key: 'tablets', label: 'Tablets' },
  { key: 'headphones', label: 'Headphones' },
  { key: 'smart_home', label: 'Smart Home' },
];

const CAT_COLORS = {
  sale_deals: '#F59E0B', tvs: '#3B82F6', phones: '#10B981',
  laptops: '#8B5CF6', tablets: '#EC4899', headphones: '#EF4444',
  smart_home: '#06B6D4', other: '#6B7280',
};

const PERIODS = [
  { value: '7d', label: 'Last 7 Days' },
  { value: '30d', label: 'Last 30 Days' },
  { value: '90d', label: 'Last 90 Days' },
];

export default function CompetitorsPage() {
  const [domain, setDomain] = useState('all');
  const [category, setCategory] = useState('all');
  const [period, setPeriod] = useState('30d');
  const [pages, setPages] = useState([]);
  const [total, setTotal] = useState(0);
  const [pageNum, setPageNum] = useState(1);
  const [pagesCount, setPagesCount] = useState(1);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const loadPages = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period, page: pageNum, limit: 40 };
      if (domain !== 'all') params.domain = domain;
      if (category !== 'all') params.category = category;
      const data = await fetchCompetitorPages(params);
      setPages(data.pages || []);
      setTotal(data.total || 0);
      setPagesCount(data.pages_count || 1);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [domain, category, period, pageNum]);

  const loadStats = useCallback(async () => {
    try { setStats(await fetchCompetitorStats()); } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);
  useEffect(() => { setPageNum(1); }, [domain, category, period]);
  useEffect(() => { loadPages(); }, [loadPages]);

  const mostActive = stats?.most_active_sale;

  return (
    <div className="space-y-6" data-testid="competitors-page">
      {/* Summary Cards */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="competitor-stats">
          {stats.domains.map(d => {
            const color = COMP_COLORS[d.domain] || '#6B7280';
            return (
              <Card key={d.domain} className="glass-card border-gray-200 rounded-none">
                <CardContent className="py-3 px-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
                    <span className="font-display text-[10px] text-gray-500 uppercase tracking-widest">{d.name}</span>
                  </div>
                  <p className="font-mono-data text-xl font-bold" style={{ color }}>{d.new_this_week}</p>
                  <p className="font-mono-data text-[10px] text-gray-400 mt-1">new pages this week</p>
                  {d.sale_pages_week > 0 && (
                    <p className="font-mono-data text-[9px] text-[#F59E0B] mt-0.5">{d.sale_pages_week} sale/deal pages</p>
                  )}
                </CardContent>
              </Card>
            );
          })}
          {/* Most Active Sale/Deal */}
          <Card className="glass-card border-gray-200 rounded-none border-l-2" style={{ borderLeftColor: COMP_COLORS[mostActive?.domain] || '#F59E0B' }}>
            <CardContent className="py-3 px-4">
              <div className="flex items-center gap-2 mb-2">
                <TrendingUp className="w-3 h-3 text-[#F59E0B]" />
                <span className="font-display text-[10px] text-gray-500 uppercase tracking-widest">Most Active</span>
              </div>
              <p className="font-display text-sm font-bold text-white">{mostActive?.name || '-'}</p>
              <p className="font-mono-data text-[10px] text-[#F59E0B] mt-1">{mostActive?.count || 0} sale pages this week</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Filters */}
      <div className="space-y-3">
        {/* Competitor Selector */}
        <div className="flex flex-wrap gap-2" data-testid="competitor-filters">
          {COMPETITORS.map(c => {
            const isActive = domain === c.key;
            return (
              <button
                key={c.key}
                onClick={() => setDomain(c.key)}
                className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider font-semibold transition-all duration-200 border flex items-center gap-2 ${
                  isActive ? "text-white" : "bg-transparent border-gray-200 text-gray-500 hover:text-gray-800"
                }`}
                style={isActive ? { background: `${c.color || '#fff'}15`, borderColor: `${c.color || '#fff'}40`, color: c.color || '#fff' } : {}}
                data-testid={`comp-filter-${c.key}`}
              >
                {c.color && <span className="w-2 h-2 rounded-sm" style={{ background: c.color }} />}
                {c.label}
              </button>
            );
          })}
        </div>

        {/* Category + Period Filters */}
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex flex-wrap gap-1.5">
            {CATEGORIES.map(c => (
              <button
                key={c.key}
                onClick={() => setCategory(c.key)}
                className={`px-2.5 py-1 text-[10px] font-display uppercase tracking-wider font-semibold transition-all duration-200 border ${
                  category === c.key
                    ? 'text-white border-current'
                    : 'text-gray-400 border-gray-200 hover:text-gray-800'
                }`}
                style={category === c.key ? { color: CAT_COLORS[c.key] || '#fff', borderColor: CAT_COLORS[c.key] || '#fff', background: `${CAT_COLORS[c.key] || '#fff'}12` } : {}}
                data-testid={`cat-filter-${c.key}`}
              >
                {c.label}
              </button>
            ))}
          </div>
          <div className="flex gap-1">
            {PERIODS.map(p => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-3 py-1.5 text-xs font-display uppercase tracking-wider font-semibold transition-all duration-200 border ${
                  period === p.value ? 'text-white border-zinc-400 bg-gray-100' : 'text-gray-400 border-gray-200 hover:text-gray-800'
                }`}
                data-testid={`period-filter-${p.value}`}
              >
                {p.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Pages Feed */}
      <Card className="glass-card border-gray-200 rounded-none" data-testid="pages-feed">
        <CardHeader className="pb-2 border-b border-gray-200 flex flex-row items-center justify-between">
          <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800 flex items-center gap-2">
            <Globe className="w-4 h-4 text-gray-500" />
            New Competitor Pages
            <Badge className="rounded-sm text-[9px] font-mono-data border-none px-1.5 py-0 bg-gray-100 text-gray-600">
              {total}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-0">
          {loading ? (
            <div className="space-y-2 py-4">{[...Array(6)].map((_, i) => <div key={i} className="h-16 bg-gray-100 animate-pulse" />)}</div>
          ) : pages.length === 0 ? (
            <div className="py-16 text-center">
              <Clock className="w-8 h-8 text-gray-400 mx-auto mb-3" />
              <p className="font-mono-data text-sm text-gray-400">No new competitor pages found</p>
              <p className="font-mono-data text-xs text-gray-400 mt-1">Pages are fetched from Ahrefs every 24 hours</p>
            </div>
          ) : (
            <>
              {/* Table Header */}
              <div className="hidden md:flex items-center py-2.5 border-b border-gray-200 text-[10px] font-display uppercase tracking-widest text-gray-400">
                <div className="w-28">Competitor</div>
                <div className="flex-1">Page URL / Title</div>
                <div className="w-32">Top Keyword</div>
                <div className="w-20 text-right">Traffic</div>
                <div className="w-24 text-right">First Seen</div>
              </div>

              {pages.map((p, i) => (
                <PageRow key={p.url || i} page={p} index={i} />
              ))}

              {pagesCount > 1 && (
                <div className="flex items-center justify-center gap-4 pt-4 border-t border-gray-200">
                  <button disabled={pageNum <= 1} onClick={() => setPageNum(n => n - 1)}
                    className="px-3 py-1 text-xs font-display uppercase text-gray-500 hover:text-white disabled:opacity-30 border border-gray-200" data-testid="prev-page">Prev</button>
                  <span className="font-mono-data text-xs text-gray-500">{pageNum} / {pagesCount}</span>
                  <button disabled={pageNum >= pagesCount} onClick={() => setPageNum(n => n + 1)}
                    className="px-3 py-1 text-xs font-display uppercase text-gray-500 hover:text-white disabled:opacity-30 border border-gray-200" data-testid="next-page">Next</button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* Pages JB Should Create */}
      <JBGapsSection />
    </div>
  );
}

function JBGapsSection() {
  const [gaps, setGaps] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [period, setPeriod] = useState('30d');

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/competitors/jb-gaps`, { params: { period, limit: 50 } });
        setGaps(res.data.gaps || []);
        setTotal(res.data.total || 0);
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    })();
  }, [period]);

  return (
    <Card className="glass-card border-gray-200 rounded-none border-t-2 border-t-orange-500/40" data-testid="jb-gaps-section">
      <CardHeader className="pb-2 border-b border-gray-200 flex flex-row items-center justify-between">
        <CardTitle className="font-display text-sm font-semibold tracking-wider uppercase text-gray-800 flex items-center gap-2">
          <Lightbulb className="w-4 h-4 text-orange-400" />
          Pages JB Should Create
          <Badge className="rounded-sm text-[9px] font-mono-data border-none px-1.5 py-0 bg-orange-500/15 text-orange-400">
            {total} gaps
          </Badge>
        </CardTitle>
        <div className="flex gap-1">
          {[{v:'7d',l:'7d'},{v:'30d',l:'30d'},{v:'90d',l:'90d'}].map(p => (
            <button key={p.v} onClick={() => setPeriod(p.v)}
              className={`px-2.5 py-1 text-[10px] font-display uppercase tracking-wider font-semibold border transition-all ${
                period === p.v ? 'text-orange-400 border-orange-500/40 bg-orange-500/10' : 'text-gray-400 border-gray-200 hover:text-gray-800'
              }`} data-testid={`gap-period-${p.v}`}>{p.l}</button>
          ))}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {loading ? (
          <div className="space-y-2 py-4">{[...Array(5)].map((_, i) => <div key={i} className="h-12 bg-gray-100 animate-pulse" />)}</div>
        ) : gaps.length === 0 ? (
          <div className="py-12 text-center">
            <AlertTriangle className="w-8 h-8 text-gray-400 mx-auto mb-3" />
            <p className="font-mono-data text-sm text-gray-400">No content gaps found</p>
            <p className="font-mono-data text-xs text-gray-400 mt-1">All recent competitor pages have JB Hi-Fi equivalents</p>
          </div>
        ) : (
          <>
            <div className="hidden md:flex items-center py-2.5 border-b border-gray-200 text-[10px] font-display uppercase tracking-widest text-gray-400">
              <div className="w-28">Competitor</div>
              <div className="flex-1">Competitor Page</div>
              <div className="w-20 text-right">Traffic</div>
              <div className="w-[280px]">Suggested JB Page Title</div>
            </div>
            {gaps.map((g, i) => {
              const color = COMP_COLORS[g.competitor_domain] || '#6B7280';
              return (
                <div key={g.competitor_url || i}
                  className="flex flex-col md:flex-row md:items-center py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors gap-1 md:gap-0"
                  data-testid={`gap-row-${i}`}>
                  <div className="w-28 flex-shrink-0">
                    <div className="flex items-center gap-2">
                      <span className="w-2 h-2 rounded-sm" style={{ background: color }} />
                      <span className="font-display text-[10px] font-semibold uppercase tracking-wider" style={{ color }}>{g.competitor_name}</span>
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <a href={g.competitor_url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-800 hover:text-white flex items-center gap-1.5 group">
                      <span className="font-mono-data truncate">{g.competitor_page_title}</span>
                      <ExternalLink className="w-3 h-3 flex-shrink-0 text-gray-400 group-hover:text-gray-600" />
                    </a>
                    {g.top_keyword && <span className="font-mono-data text-[10px] text-gray-400">Keyword: {g.top_keyword}</span>}
                  </div>
                  <div className="w-20 text-right flex-shrink-0">
                    <span className="font-mono-data text-xs font-bold text-gray-800">
                      {g.traffic >= 1000 ? `${(g.traffic / 1000).toFixed(1)}K` : g.traffic || 0}
                    </span>
                  </div>
                  <div className="w-[280px] flex-shrink-0">
                    <span className="font-mono-data text-[11px] text-orange-300 bg-orange-500/5 border border-orange-500/20 px-2 py-1 inline-block truncate max-w-full">
                      {g.suggested_jb_title}
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </CardContent>
    </Card>
  );
}

function PageRow({ page, index }) {
  const compColor = COMP_COLORS[page.domain] || '#6B7280';
  const catColor = CAT_COLORS[page.category] || '#6B7280';
  const catLabel = CATEGORIES.find(c => c.key === page.category)?.label || page.category;
  const firstSeen = page.first_seen ? new Date(page.first_seen) : null;
  const daysSince = firstSeen ? Math.floor((Date.now() - firstSeen.getTime()) / 86400000) : null;

  // Extract clean path from URL for display
  let displayPath = page.url || '';
  try {
    const u = new URL(displayPath);
    displayPath = u.pathname + u.search;
    if (displayPath.length > 80) displayPath = displayPath.slice(0, 77) + '...';
  } catch { /* keep as is */ }

  return (
    <div
      className="flex flex-col md:flex-row md:items-center py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors duration-150 animate-fade-in-up gap-1 md:gap-0"
      style={{ animationDelay: `${index * 20}ms` }}
      data-testid={`page-row-${index}`}
    >
      {/* Competitor */}
      <div className="w-28 flex-shrink-0">
        <div className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: compColor }} />
          <span className="font-display text-[10px] font-semibold uppercase tracking-wider" style={{ color: compColor }}>
            {page.competitor_name || page.domain}
          </span>
        </div>
      </div>

      {/* URL + Title + Category */}
      <div className="flex-1 min-w-0">
        <a href={page.url} target="_blank" rel="noopener noreferrer" className="text-xs text-gray-800 hover:text-white transition-colors flex items-center gap-1.5 group">
          <span className="font-mono-data truncate">{displayPath}</span>
          <ExternalLink className="w-3 h-3 flex-shrink-0 text-gray-400 group-hover:text-gray-600" />
        </a>
        <div className="flex items-center gap-2 mt-1">
          {page.title && (
            <span className="font-mono-data text-[10px] text-gray-400 truncate max-w-[300px]">{page.title}</span>
          )}
          <span className="text-[9px] font-mono-data px-1.5 py-0 flex-shrink-0" style={{ color: catColor, background: `${catColor}12`, border: `1px solid ${catColor}25` }}>
            {catLabel}
          </span>
        </div>
      </div>

      {/* Top Keyword */}
      <div className="w-32 flex-shrink-0">
        {page.top_keyword && (
          <div className="flex items-center gap-1">
            <Search className="w-2.5 h-2.5 text-gray-400 flex-shrink-0" />
            <span className="font-mono-data text-[10px] text-gray-600 truncate">{page.top_keyword}</span>
          </div>
        )}
        {page.top_keyword_volume > 0 && (
          <span className="font-mono-data text-[9px] text-gray-400 ml-3.5">{page.top_keyword_volume.toLocaleString()} vol/mo</span>
        )}
      </div>

      {/* Traffic */}
      <div className="w-20 text-right flex-shrink-0">
        <div className="flex items-center justify-end gap-1">
          <BarChart3 className="w-2.5 h-2.5 text-gray-400" />
          <span className="font-mono-data text-xs font-bold text-gray-800">
            {page.monthly_traffic >= 1000 ? `${(page.monthly_traffic / 1000).toFixed(1)}K` : page.monthly_traffic || 0}
          </span>
        </div>
        <span className="font-mono-data text-[9px] text-gray-400">/month</span>
      </div>

      {/* First Seen */}
      <div className="w-24 text-right flex-shrink-0">
        {firstSeen && (
          <>
            <span className="font-mono-data text-[10px] text-gray-600">
              {firstSeen.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}
            </span>
            {daysSince !== null && daysSince <= 7 && (
              <Badge className="ml-1 rounded-sm text-[8px] font-mono-data border-none px-1 py-0 bg-emerald-500/15 text-emerald-400">NEW</Badge>
            )}
          </>
        )}
      </div>
    </div>
  );
}
